import * as React from 'react'
import { redirect } from 'react-router-dom'

import {
  ExpenseBudgetDetailRouteDocument,
  ExpensesRoutesVisibilityDocument,
} from 'generated/resources/graphql'

import { checkFeatures } from 'modules/core/hooks/useFeatures'
import i18n from 'modules/core/lib/i18n'
import { buildNavigation } from 'modules/core/lib/react-router'

import { PageDateFiltersProvider } from 'modules/expenses/lib/PageDateFiltersContext'
import { WithDateFilters } from 'modules/expenses/lib/WithDateFilters'

import BudgetDetailHeader from './BudgetDetailHeader'

const ReroutingIndex = React.lazy(() => import('modules/finance/pages/ReroutingIndex'))

const ExpensesNewCardPage = React.lazy(() => import('modules/expenses/pages/NewCardPage'))

const GroupExpenseDetailPage = React.lazy(
  () => import('modules/expenses/pages/Groups/DetailPage/GroupExpenseDetailPage')
)

const ExpensesReportsPage = React.lazy(() => import('modules/expenses/pages/ExpensesReportsPage'))
const ExpenseDetailPage = React.lazy(() => import('modules/expenses/pages/ExpenseDetailPage'))
const ExpensesListPage = React.lazy(() => import('modules/expenses/pages/ExpensesListPage'))
const ExpensesListTab = React.lazy(() => import('modules/expenses/pages/ExpensesListTab'))
const GroupDetailPage = React.lazy(() => import('modules/expenses/pages/Groups/DetailPage'))
const GroupEditPage = React.lazy(() => import('modules/expenses/pages/Groups/EditPage'))
const GroupsListTab = React.lazy(() => import('modules/expenses/pages/GroupsListTab'))
const NewCardPage = React.lazy(() => import('modules/expenses/pages/NewCardPage'))
const RootPage = React.lazy(() => import('modules/expenses/pages/RootPage'))
const ExporterPage = React.lazy(() => import('modules/expenses/pages/exporter/ExporterPage'))
const BudgetDetailsPage = React.lazy(() => import('modules/finance/pages/BudgetDetailsPage'))
const ExpenseBudgetRecordsTab = React.lazy(
  () => import('modules/finance/pages/ExpenseBudgetDetailPage/RecordsTab')
)
const ExpenseBudgetEmployeesTab = React.lazy(
  () => import('modules/finance/pages/ExpenseBudgetDetailPage/EmployeesTab')
)
const BudgetListPage = React.lazy(() => import('modules/finance/pages/BudgetListPage'))

const NewBudgetPage = React.lazy(() => import('modules/finance/pages/NewBudgetPage'))
const NewSpendingPoliciesPage = React.lazy(
  () => import('modules/finance/pages/NewSpendingPoliciesPage')
)
const SpendingPoliciesListPage = React.lazy(
  () => import('modules/finance/pages/SpendingPoliciesListPage')
)
const SpendingPoliciesPage = React.lazy(() => import('modules/finance/pages/SpendingPoliciesPage'))
const SpendingPoliciesDetailPage = React.lazy(
  () => import('modules/finance/pages/SpendingPoliciesDetailPage')
)
const expensesSubModuleRoutes = buildNavigation((navigation) => {
  return [
    {
      path: navigation.finance.spending.expenses.path,
      element: <RootPage />,
      handle: {
        meta: ({ watchQuery, session }) => {
          return watchQuery({
            query: ExpensesRoutesVisibilityDocument,
            variables: { companyId: session.company.id },
          }).map(({ data }) => {
            const {
              sidebarSeeExpensesSpendingPoliciesSubLevelItem,
              sidebarSeeExpensesBudgetsSubLevelItem,
              sidebarSeeExpensesExpensesSubLevelItem,
            } = data.expenses._policies

            const expensableGroupsEnabled =
              data.expenses.expensableGroupsSettingsConnection?.nodes[0]?.enabled ?? false

            return {
              title: i18n.t('expenses_v2.breadcrumbs.expenses'),
              illustration: 'wallet',
              subNavigation: [
                {
                  id: 'employee-expenses',
                  title: i18n.t('expenses_v2.breadcrumbs.expenses'),
                  href: navigation.finance.spending.expenses.list.expenses.fullPath(),
                  isVisible: sidebarSeeExpensesExpensesSubLevelItem.can,
                },
                {
                  id: 'expense-groups',
                  title: i18n.t('expenses_v2.list.tabs.groups'),
                  href: navigation.finance.spending.expenses.list.groups.fullPath(),
                  isVisible: sidebarSeeExpensesExpensesSubLevelItem.can && expensableGroupsEnabled,
                },
                {
                  id: 'expenses-reports',
                  title: i18n.t('expenses_v2.tabs.expenses_reports_v2'),
                  href: navigation.finance.spending.expenses.reports.fullPath(),
                  isVisible: sidebarSeeExpensesExpensesSubLevelItem.can,
                },
                {
                  id: 'budgets',
                  title: i18n.t('budgets.navbar.budgets'),
                  href: navigation.finance.spending.expenses.budgets.fullPath(),
                  isVisible: sidebarSeeExpensesBudgetsSubLevelItem.can,
                },
                {
                  id: 'spending-policies',
                  title: i18n.t('spending_policies.navbar.spending_policies'),
                  href: navigation.finance.spending.expenses.spendingPolicies.fullPath(),
                  isVisible: sidebarSeeExpensesSpendingPoliciesSubLevelItem.can,
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <ReroutingIndex />,
        },
        {
          path: navigation.finance.spending.expenses.list.path,
          children: [
            {
              index: true,
              loader: () => redirect(navigation.finance.spending.expenses.list.expenses.fullPath()),
            },
            {
              path: navigation.finance.spending.expenses.list.expenses.path,
              handle: {
                meta: () => ({
                  title: i18n.t('expenses_v2.breadcrumbs.expenses'),
                  illustration: 'wallet',
                }),
              },
              children: [
                {
                  path: navigation.finance.spending.expenses.list.expenses.exporter.path,
                  element: <ExporterPage />,
                },
                {
                  path: navigation.finance.spending.expenses.list.expenses.expenseId.path,
                  element: <ExpenseDetailPage />,
                },
              ],
              element: <ExpensesListTab />,
            },
            {
              path: navigation.finance.spending.expenses.list.groups.path,
              handle: {
                meta: () => {
                  return {
                    title: i18n.t('expenses_v2.breadcrumbs.expenses'),
                    illustration: 'wallet',
                  }
                },
              },
              element: <GroupsListTab />,
            },
          ],
          element: (
            <PageDateFiltersProvider>
              <ExpensesListPage />
            </PageDateFiltersProvider>
          ),
        },
        {
          path: navigation.finance.spending.expenses.reports.path,
          element: (
            <PageDateFiltersProvider>
              <ExpensesReportsPage />
            </PageDateFiltersProvider>
          ),
          handle: {
            meta: () => ({
              title: i18n.t('expenses_v2.tabs.expenses_reports'),
            }),
          },
        },
        {
          path: navigation.finance.spending.expenses.groups.path,
          children: [
            {
              index: true,
              loader: () => redirect(navigation.finance.spending.expenses.list.groups.fullPath()),
            },
            {
              path: navigation.finance.spending.expenses.groups.id.path,
              handle: {
                resource: true,
                meta: () => ({
                  title: i18n.t('expenses_v2.breadcrumbs.group_id'),
                }),
              },
              children: [
                {
                  path: navigation.finance.spending.expenses.groups.id.edit.path,
                  element: <GroupEditPage />,
                },
                {
                  path: navigation.finance.spending.expenses.groups.id.expenseId.path,
                  element: <GroupExpenseDetailPage />,
                },
              ],
              element: <GroupDetailPage />,
            },
          ],
          element: <WithDateFilters />,
        },
        {
          path: navigation.finance.spending.expenses.new_card.path,
          element: <NewCardPage />,
          handle: {
            meta: () => ({
              title: i18n.t('expenses_v2.modals.new_card.title'),
            }),
          },
        },
        {
          path: navigation.finance.spending.expenses.budgets.path,
          handle: {
            meta: () => ({ title: i18n.t('budgets.detail.budgets') }),
          },
          children: [
            {
              index: true,
              loader: () => redirect(navigation.finance.spending.expenses.budgets.list.fullPath()),
            },
            {
              path: navigation.finance.spending.expenses.budgets.list.path,
              element: <BudgetListPage />,
              children: [
                {
                  path: navigation.finance.spending.expenses.budgets.list.new.path,
                  element: <NewBudgetPage />,
                },
              ],
            },
            {
              path: navigation.finance.spending.expenses.budgets.detail.path,
              handle: {
                resource: true,
                meta: ({ watchQuery, params, session }) => {
                  return watchQuery({
                    query: ExpenseBudgetDetailRouteDocument,
                    variables: {
                      id: params.budgetId ?? '',
                      companyId: session.company.id,
                    },
                    fetchPolicy: 'cache-and-network',
                  }).map(({ data }) => {
                    const budget = data?.finance?.budgetsConnection?.nodes[0]
                    const trainingGroupsCount =
                      data?.finance?.budgetMovementsConnection?.totalCount ?? undefined

                    const { has_dev_training_budgets } = checkFeatures(
                      ['dev_training_budgets'],
                      data?.features?.featuresConnection?.nodes
                    )

                    const isExpenseBudget = budget?.budgetableBy === 'expensable'
                    const showTabs = isExpenseBudget && has_dev_training_budgets

                    return {
                      title: budget?.name,
                      headerComponent: (
                        <BudgetDetailHeader
                          budget={budget}
                          trainingGroupsCount={trainingGroupsCount}
                        />
                      ),
                      subNavigation: showTabs
                        ? [
                            {
                              title: i18n.t('budgets.expense_budget_detail.tabs.records'),
                              href: navigation.finance.spending.expenses.budgets.detail.records.fullPath(
                                {
                                  budgetId: params.budgetId ?? '',
                                }
                              ),
                              index: true,
                            },
                            {
                              title: i18n.t('budgets.expense_budget_detail.tabs.employees'),
                              href: navigation.finance.spending.expenses.budgets.detail.employees.fullPath(
                                {
                                  budgetId: params.budgetId ?? '',
                                }
                              ),
                            },
                          ]
                        : undefined,
                    }
                  })
                },
              },
              element: <BudgetDetailsPage />,
              children: [
                {
                  path: navigation.finance.spending.expenses.budgets.detail.records.path,
                  element: <ExpenseBudgetRecordsTab />,
                },
                {
                  path: navigation.finance.spending.expenses.budgets.detail.employees.path,
                  element: <ExpenseBudgetEmployeesTab />,
                },
              ],
            },
          ],
        },
        {
          path: navigation.finance.spending.expenses.spendingPolicies.path,
          handle: {
            meta: () => ({ title: i18n.t('expenses_v2.breadcrumbs.policies') }),
          },
          element: <SpendingPoliciesPage />,
          children: [
            {
              index: true,
              loader: () =>
                redirect(navigation.finance.spending.expenses.spendingPolicies.list.fullPath()),
            },
            {
              path: navigation.finance.spending.expenses.spendingPolicies.list.path,
              children: [
                {
                  path: navigation.finance.spending.expenses.spendingPolicies.list.new.path,
                  element: <NewSpendingPoliciesPage />,
                },
              ],
              element: <SpendingPoliciesListPage />,
            },
            {
              path: navigation.finance.spending.expenses.spendingPolicies.detail.path,

              handle: {
                resource: true,
                meta: () => ({ title: i18n.t('expenses_v2.breadcrumbs.edit_policy') }),
              },
              element: <SpendingPoliciesDetailPage />,
            },
          ],
        },
      ],
    },
    {
      path: navigation.finance.spending.expenses.new_card.path,
      element: <ExpensesNewCardPage />,
      handle: { meta: () => ({ title: i18n.t('expenses_v2.modals.new_card.title') }) },
    },
  ]
})

export default expensesSubModuleRoutes
